import { useWindowSize } from '@Components/Utilities/WindowSize';
import { useState, useEffect, useRef } from 'react';
import { getToken } from "firebase/messaging";
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Popper from '@mui/material/Popper';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { useFirebaseMessaging } from '@Components/Utilities/Firebase';
import translator from '@Components/Utilities/Translator';
import { pushNotificationLocalStorageItems } from '@Components/Utilities/SandhillsConstants';

const PushNotificationButton = (props) => {
    const routes = useRecoilValue(atoms.routes);
    const windowSize = useWindowSize();

    const hoverPopperRef = useRef(null);
    const originalRightRef = useRef(null);
    const originalBottomRef = useRef(null);
    const scrollToTopRef = useRef(null);
    const subscriptionPopperRef = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [browserIsSupported, setBrowserIsSupported] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [permission, setPermission] = useState(false);
    const [subscribed, setSubscribed] = useState(false);
    const [showHoverPopper, setShowHoverPopper] = useState(false);
    const [showSubscriptionMessagePopper, setShowSubscriptionMessagePopper] = useState(false);

    const open = Boolean(anchorEl);
    const id = open ? ('iterable-bell-launcher-subscription-popper') : undefined;
    const firebaseMessaging = useFirebaseMessaging()?.firebaseMessaging;
    const vapidKey = useFirebaseMessaging()?.vapidKey;

    useEffect(() => {
        adjustPosition();

        const observer = new MutationObserver(adjustPosition);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
        };
    }, [windowSize.width]);

    useEffect(() => {
        let iterableBellContainer = document.getElementById('iterable-bell-launcher-container');
        if (iterableBellContainer) {
            iterableBellContainer.addEventListener('mouseenter', () => {
                if (!showHoverPopper) {
                    setShowHoverPopper(true);
                }
            });
            iterableBellContainer.addEventListener('mouseleave', () => {
                if (showHoverPopper) {
                    setShowHoverPopper(false);
                }
            });
        }
    }, [showHoverPopper]);

    useEffect(() => {
        if (typeof window !== 'undefined' && "Notification" in window) {
            setBrowserIsSupported(true);
            if (Notification.permission === 'granted') {
                setPermission(true);
                const iterableSubscriptionId = localStorage?.getItem(pushNotificationLocalStorageItems.IterableSubscriptionId);
                const subscribeToIterable = localStorage?.getItem(pushNotificationLocalStorageItems.SubscribeToIterable);
                const isSubscribed = localStorage?.getItem(pushNotificationLocalStorageItems.IsSubscribed);

                if ((iterableSubscriptionId != null) || (subscribeToIterable && subscribeToIterable == "true") || (isSubscribed && isSubscribed == "true")) {
                    setSubscribed(true);
                    if (shouldRefreshSubscription() || subscribeToIterable || (isSubscribed == "true" && !iterableSubscriptionId)) {
                        getFirebaseToken(false, true);
                    }
                } else if (!isSubscribed) {
                    // this means user has cleared local storage or is on a new device/browser
                    // determine subscription status from backend
                    getFirebaseToken(false, false);
                }
            }

        }
        else {
            setBrowserIsSupported(false);
        }
    }, []);

    const getUserSubscription = (token) => {
        sandhillsRequest({
            url: routes.GetWebPushUserSubscription,
            method: 'post',
            data: {
                token: token,
                subscriptionID: 0,
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            if (data) {
                if (data.Status == "ACTIVE") {
                    updateSubscription(token, true, data.UserSubscriptionID, data.UserAuthID);
                } else {
                    setIterableLocalStorageItems(false, 0);
                }
            }
            else {
                setIterableLocalStorageItems(false, 0);
            }
        });
    };


    const updateSubscription = (token, subscribe, subscriptionID = 0, userAuthID = 0) => {
        sandhillsRequest({
            url: routes.SaveUserWebPushSubscription,
            method: 'post',
            data: {
                token: token,
                subscriptionID: localStorage?.getItem(pushNotificationLocalStorageItems.IterableSubscriptionId) ?? subscriptionID,
                subscribe: subscribe,
                userAuthID: userAuthID
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
                setSubscribed(subscribe);
                setIterableLocalStorageItems(subscribe, subscribe ? data.UserSubscriptionID : 0);
            if (subscribe) {
                setAnchorEl(null);
                setShowHoverPopper(false);
                setShowSubscriptionMessagePopper(true);
                setTimeout(() => setShowSubscriptionMessagePopper(false), 2500);
            } else {
                setPermission(false);
                setAnchorEl(null);
                setShowHoverPopper(false);
                setShowSubscriptionMessagePopper(true);
                setTimeout(() => setShowSubscriptionMessagePopper(false), 2500);
            }
        });

    };

    const shouldRefreshSubscription = () => {
        // check if the last update was more than 30 days ago
        const lastUpdate = localStorage?.getItem(pushNotificationLocalStorageItems.PushSubscriptionLastUpdate);
        if (!lastUpdate) return true; // no record of last update, need to subscribe
        if (lastUpdate) {
            const lastUpdateDate = new Date(parseInt(lastUpdate));
            const currentDate = new Date();
            const diffTime = Math.abs(currentDate - lastUpdateDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays >= 30;
        };
    };

    const handleClick = () => {
        if (browserIsSupported) {
            setAnchorEl(anchorEl ? null : document.getElementById('iterable-bell-launcher-container'));
        }
    };

    const handleSubscribe = () => {
        // request permission to send notifications
        if (browserIsSupported) {
            Notification.requestPermission().then(async (perm) => {
                if (perm === 'granted') {
                    setPermission(true);
                    getFirebaseToken(true, true);
                }
            });
        }
    };

    const styles = {
        popper: {
            zIndex: 1004,
            backgroundColor: 'white',
            borderRadius: '5px',
            boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.1)',
        },
        box: {
            width: '300px',
            backgroundColor: 'black',
            borderRadius: '5px',
            boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.1)',
            padding: '10px',
            color: 'white',
            alignContent: 'center',
            alignItems: 'center',
            position: 'relative',
            fontSize: '14px'
        },
        subscriptionBox: {
            width: '250px',
            backgroundColor: 'white',
            borderRadius: '5px',
            boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.1)',
            padding: '10px',
            color: 'black',
            alignContent: 'center',
            alignItems: 'center',
            position: 'relative'
        },
        subscriptionButton: {
            fontSize: '12px',
            width: '100%',
            color: '#fff',
            backgroundColor: '#780113',
        },
        bellButton: {
            display: 'flex',
            minWidth: '40px',
            maxWidth: '40px',
            height: '40px',
            padding: '5px',
            margin: '0 auto',
            fontSize: '16px',
            fontWeight: '700',
            textAlign: 'center',
            borderRadius: '50%',
            color: '#fff',
            backgroundColor: 'transparent',
            border: '2px solid #fff',
            boxShadow: 'none',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        }
    };

    const popperModifiers = [
        {
            name: 'flip',
            enabled: true,
            options: {
                altBoundary: true,
                rootBoundary: 'document',
                padding: 8,
            },
        },
        {
            name: 'preventOverflow',
            enabled: true,
            options: {
                altAxis: false,
                altBoundary: true,
                tether: true,
                rootBoundary: 'document',
                padding: 8,
            },
        },
    ];

    function getFirebaseToken(isOnClick = false, update = true) {
        if (firebaseMessaging) {
            getToken(firebaseMessaging, { vapidKey: vapidKey }).then((token) => {
                if (token) {
                    if (!update) {
                        getUserSubscription(token);
                    }
                    else if (shouldRefreshSubscription() || isOnClick) {
                        updateSubscription(token, true);
                    }
                }
            }).catch((err) => {
                console.log('An error occurred while retrieving token. ', err);
                localStorage?.setItem(pushNotificationLocalStorageItems.SubscribeToIterable, 'true'); // try subscribing later if there was an error
            });
        }
    }

    const setIterableLocalStorageItems = (subscribe, subscriptionID) => {
        if (subscribe) {
            localStorage?.setItem(pushNotificationLocalStorageItems.IsSubscribed, 'true');
            localStorage?.setItem(pushNotificationLocalStorageItems.IterableSubscriptionId, subscriptionID);
            localStorage?.setItem(pushNotificationLocalStorageItems.PushSubscriptionLastUpdate, Date.now());
            localStorage?.removeItem(pushNotificationLocalStorageItems.SubscribeToIterable);
        }
        else {
            localStorage?.setItem(pushNotificationLocalStorageItems.IsSubscribed, 'false');
            localStorage?.removeItem(pushNotificationLocalStorageItems.IterableSubscriptionId);
            localStorage?.removeItem(pushNotificationLocalStorageItems.PushSubscriptionLastUpdate);
        }
    };

    const handleUnsubscribe = () => {
        if (subscribed && permission && browserIsSupported) {
            getToken(firebaseMessaging, { vapidKey: vapidKey }).then((token) => {
                if (token) {
                    updateSubscription(token, false);
                }
            });
        }
    };

    const adjustPosition = () => {
        let iterableBellLauncher = document.getElementById('iterable-bell-launcher-container');
        if (iterableBellLauncher) {
            scrollToTopRef.current = document.getElementsByClassName('scrollToTop')[0];
            let hasListingControlBar = document.getElementsByClassName('mobile-listing-control-bar').length > 0;
            let hasMobileOptionBar = document.getElementsByClassName('mobile-option-bar').length > 0;
            let hasDetailsContactBar = document.getElementsByClassName('detail-contact-bar').length > 0;
            let isSubscribed = document.getElementsByClassName('subscribed iterable-bell-launcher-container')[0];
            let hasIntercom = document.getElementsByClassName('intercom-launcher').length > 0;

            if (!hasIntercom && window.Intercom) {
                hasIntercom = window.Intercom.booted;
            }

            if (originalRightRef.current === null) {
                originalRightRef.current = iterableBellLauncher.style.right
            }
            if (originalBottomRef.current === null) {
                originalBottomRef.current = iterableBellLauncher.style.bottom
            }

            iterableBellLauncher.style.right = originalRightRef.current;
            iterableBellLauncher.style.bottom = originalBottomRef.current;

            // Set style if intercom button is present or if "Scroll To Top" button is on the right of the screen
            if (hasIntercom || (scrollToTopRef.current && window.getComputedStyle(scrollToTopRef.current).right === '25px')) {
                iterableBellLauncher.style.bottom = '20px';
                iterableBellLauncher.style.right = '85px';
            }

            if (hasMobileOptionBar || hasDetailsContactBar) {
                iterableBellLauncher.style.bottom = isSubscribed ? '83px' : '75px';
            }
            else if (isSubscribed) {
                iterableBellLauncher.style.bottom = '20px';
            }

            if (hasListingControlBar && windowSize.width < 775) {
                const listingControlBar = document.getElementsByClassName('mobile-listing-control-bar')[0].getBoundingClientRect();

                if (listingControlBar.height > 60) {
                    iterableBellLauncher.style.bottom = isSubscribed ? '97px' : '89px';
                }
                else {
                    iterableBellLauncher.style.bottom = isSubscribed ? '64px' : '56px';
                }

                const iterableRect = iterableBellLauncher.getBoundingClientRect();
                if (listingControlBar.top < iterableRect.bottom) {
                    iterableBellLauncher.style.bottom = '20px';
                }
            }
        }

    };

    return <>
        <div id='iterable-bell-launcher-container' className={subscribed && !open && !showSubscriptionMessagePopper ? 'subscribed iterable-bell-launcher-container' : 'iterable-bell-launcher-container'}>
            <div id='iterable-bell-launcher'>
                <div id='iterable-bell-launcher-inner-div'>
                    <Button id='iterable-bell-launcher-btn' onClick={handleClick} className='iterable-bell-launcher-btn'
                        variant="contained"
                        aria-label={!browserIsSupported ? "Sorry, your browser or device does not support push notifications." : subscribed ? translator.translate("sandhills.managenotifications") : translator.translate("sandhills.subscribetonotifications")}
                        sx={styles.bellButton}>
                        <NotificationsIcon fontSize={'medium'} aria-hidden={true} />
                    </Button>
                </div>
                {showSubscriptionMessagePopper &&
                    <Popper
                        open={showSubscriptionMessagePopper}
                        anchorEl={document.getElementById('iterable-bell-launcher-container')}
                        placement="left"
                        disablePortal={true}
                        sx={styles.popper}
                        modifiers={popperModifiers}
                    >
                        <Box sx={styles.box}>
                            <span>{errorMessage != "" ? errorMessage :
                                props.IsAuctionSite ?
                                    (subscribed ? translator.translate('sandhills.thankyouforsubscribingtoauctionnotifications') : translator.translate('sandhills.youwontreceivenotificationsagain')) :
                                    (subscribed ? translator.translate('sandhills.thanksforsubscribing') : translator.translate('sandhills.tradepubwebsite.mvc.youhavebeenunsubscribed'))}
                            </span>
                        </Box>
                    </Popper>}
                {showHoverPopper && !open &&
                    <Popper
                        id='iterable-bell-launcher-hover-popper'
                        popperRef={hoverPopperRef}
                        placement="left"
                        disablePortal={true}
                        open={showHoverPopper && !open}
                        anchorEl={document.getElementById('iterable-bell-launcher-container')}
                        sx={styles.popper}
                        modifiers={popperModifiers}
                    >
                        <Box sx={styles.box}>
                            <span>
                                {!browserIsSupported ? "Sorry, your browser or device does not support push notifications." :
                                    props.IsAuctionSite ?
                                        (subscribed ? translator.translate('sandhills.youaresubscribedtoauctionnotifications') : translator.translate('sandhills.subscribetoauctionnotifications')) :
                                        (subscribed ? translator.translate('sandhills.youaresubscribedtonotifications') : translator.translate('sandhills.subscribetonotifications'))}
                            </span>
                        </Box>
                    </Popper>}
                <Popper
                    id={id}
                    popperRef={subscriptionPopperRef}
                    placement="top-end"
                    disablePortal={true}
                    open={open}
                    anchorEl={anchorEl}
                    sx={styles.popper}
                    modifiers={popperModifiers}
                >
                    <Box sx={styles.subscriptionBox}>
                        <>
                            <div className='popper-title'>
                                <span>{props.IsAuctionSite ? translator.translate('sandhills.auctionnotifications') : translator.translate("sandhills.managenotifications")}</span>
                            </div>
                            <div className='popper-border'></div>
                            <div className='push-notification-text-outer-container'>
                                <div className='popper-img-container'>
                                    <img src={props.AppLogo} alt="Notifications" />
                                </div>
                                <div className='push-notification-text-container'>
                                    <div className="push-notification-text push-notification-text-short"></div>
                                    <div className="push-notification-text"></div>
                                    <div className="push-notification-text push-notification-text-medium"></div>
                                    <div className="push-notification-text"></div>
                                    <div className="push-notification-text push-notification-text-medium"></div>
                                </div>
                            </div>
                            <div>
                                <Button variant="contained" sx={styles.subscriptionButton} onClick={subscribed ? handleUnsubscribe : handleSubscribe}>{subscribed ? translator.translate('sandhills.tradepubwebsite.mvc.unsubscribe') : translator.translate('sandhills.subscribe')}</Button>
                            </div>
                        </>
                    </Box>
                </Popper>
            </div>
        </div>
    </>
};

const togglePushNotifications = (show) => {
    let iterableBellLauncher = document.getElementById('iterable-bell-launcher-container');
    if (iterableBellLauncher) {
        iterableBellLauncher.style.display = (show ? 'block' : 'none');
    }
};

export default PushNotificationButton;
export { togglePushNotifications };